import { useState, useEffect } from 'react';
import { FileText, CheckCircle, Clock, RefreshCw, Loader2, Play, Download, AlertCircle, PlayCircle } from 'lucide-react';
import Button from './ui/Button';
import Select from './ui/Select';
import { toast } from 'sonner';

type TimeRange = '1year' | '2years' | '3years' | '5years' | '10years';

interface QuarterlyFiling {
  filing_id: string;
  company_identifier: string;
  fiscal_year: number;
  fiscal_period: string;   // e.g. "Q2 2025" or "Annual 2025" (backend includes year)
  filing_date: string;
  report_date?: string;
  form_type: string;
  source: string;
  report_type: string;
  accession_number?: string;
  processed?: boolean;     // true if Neo4j has financial data for this filing
}

interface ProcessedReport {
  reportId: string;
  fiscalYear?: number;
  fiscalQuarter?: number;
  period?: string;
  filingDate?: string;
  status?: string;
  xbrlExtracted?: boolean;
  aiExtracted?: boolean;
  segmentsExtracted?: boolean;
}

interface QuarterlyFilingsTableProps {
  companyId: string;
  companyName: string;
  cik: string;
  processedReports?: ProcessedReport[];
  onRefreshNeeded?: () => void;
}

export default function QuarterlyFilingsTable({
  companyId,
  companyName,
  cik,
  processedReports = [],
  onRefreshNeeded
}: QuarterlyFilingsTableProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('2years');
  const [filings, setFilings] = useState<QuarterlyFiling[]>([]);
  const [filingStatuses, setFilingStatuses] = useState<Record<string, string>>({});  // Changed from boolean to string to store actual status
  const [filingDetails, setFilingDetails] = useState<Record<string, ProcessedReport | null>>({});  // Store full report details
  const [loading, setLoading] = useState(false);
  const [processingReports, setProcessingReports] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any>(null);

  // Fetch quarterly filings when component mounts or time range changes
  useEffect(() => {
    if (cik) {
      fetchQuarterlyFilings();
    }
  }, [cik, timeRange]);

  // Re-check statuses when processedReports changes
  useEffect(() => {
    if (filings.length > 0) {
      checkFilingStatuses(filings);
    }
  }, [processedReports]);

  // Poll job status when jobId is set (Phase 3)
  useEffect(() => {
    if (!jobId) return;

    console.log(`[Batch Processing] Starting polling for job: ${jobId}`);

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/research/jobs/${jobId}`);

        if (!response.ok) {
          console.error('[Batch Processing] Failed to fetch job status');
          return;
        }

        const progress = await response.json();
        console.log(`[Batch Processing] Job progress:`, progress);

        setJobProgress(progress);

        // Check if job completed or failed
        if (progress.status === 'completed') {
          clearInterval(pollInterval);
          console.log('[Batch Processing] Job completed successfully!');
          toast.success(`Batch processing completed! Processed ${progress.processedReports} reports.`);

          // Reset state and refresh filings
          setBatchProcessing(false);
          setJobId(null);
          setJobProgress(null);
          await fetchQuarterlyFilings(true);  // Silent refresh
          onRefreshNeeded?.();
        } else if (progress.status === 'failed') {
          clearInterval(pollInterval);
          console.error('[Batch Processing] Job failed:', progress.message);
          toast.error(`Batch processing failed: ${progress.message}`);

          // Reset state
          setBatchProcessing(false);
          setJobId(null);
          setJobProgress(null);
        }
      } catch (error) {
        console.error('[Batch Processing] Error polling job status:', error);
      }
    }, 3000);  // Poll every 3 seconds

    // Cleanup on unmount
    return () => {
      console.log('[Batch Processing] Stopping polling');
      clearInterval(pollInterval);
    };
  }, [jobId]);

  const fetchQuarterlyFilings = async (silent: boolean = false) => {
    setLoading(true);
    try {
      // Fetch filings from adapter endpoint
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/quarterly/filings/US/${cik}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch quarterly filings');
      }

      const data = await response.json();
      const allFilings = data.filings || [];

      // Sort by filing date descending (most recent first)
      const sortedFilings = [...allFilings].sort((a: QuarterlyFiling, b: QuarterlyFiling) => {
        return new Date(b.filing_date).getTime() - new Date(a.filing_date).getTime();
      });

      // Filter by actual date range (not just number of quarters)
      const now = new Date();
      const yearsToShow = timeRange === '1year' ? 1 :
                          timeRange === '2years' ? 2 :
                          timeRange === '3years' ? 3 :
                          timeRange === '5years' ? 5 :
                          10; // 10years

      const cutoffDate = new Date(now);
      cutoffDate.setFullYear(cutoffDate.getFullYear() - yearsToShow);

      // Filter filings within the date range
      const filtered = sortedFilings.filter((filing: QuarterlyFiling) => {
        const filingDate = new Date(filing.filing_date);
        return filingDate >= cutoffDate;
      });

      setFilings(filtered);

      // Check processing status for each filing
      await checkFilingStatuses(filtered);
    } catch (error) {
      console.error('Error fetching quarterly filings:', error);
      // Only show toast if not in silent mode (when called after refresh)
      if (!silent) {
        toast.error('Failed to load quarterly filings');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkFilingStatuses = async (filingsToCheck: QuarterlyFiling[]) => {
    const statuses: Record<string, string> = {};
    const details: Record<string, ProcessedReport | null> = {};

    filingsToCheck.forEach((filing) => {
      // Fast path: use the `processed` flag returned directly by the SEC EDGAR endpoint
      if (filing.processed !== undefined) {
        statuses[filing.filing_id] = filing.processed ? 'Processed' : 'Not Processed';
        details[filing.filing_id] = null;
        return;
      }

      // Fallback: match against processedReports from parent (non-US adapters)
      const matchingReport = processedReports.find(report => {
        if (report.filingDate && filing.filing_date && report.filingDate === filing.filing_date) {
          return true;
        }
        if (report.fiscalYear === filing.fiscal_year) {
          if (filing.report_type === 'Annual Report' || filing.report_type === 'annual') {
            if (report.period) return report.period.includes('FY') || (!report.fiscalQuarter && !report.period.match(/Q[1-4]/));
            return !report.fiscalQuarter;
          }
          if (report.period && filing.fiscal_period) return report.period.includes(filing.fiscal_period);
          if (report.fiscalQuarter) return filing.fiscal_period === `Q${report.fiscalQuarter}`;
        }
        if (report.reportId && filing.accession_number) {
          return report.reportId.includes(filing.accession_number.replace(/-/g, ''));
        }
        return false;
      });

      statuses[filing.filing_id] = matchingReport?.status || 'Not Processed';
      details[filing.filing_id] = matchingReport || null;
    });

    setFilingStatuses(statuses);
    setFilingDetails(details);
  };

  const handleProcessReport = async (filing: QuarterlyFiling) => {
    const reportKey = filing.filing_id;
    setProcessingReports(prev => new Set(prev).add(reportKey));

    try {
      // Use accession_number if available, otherwise fall back to filing_id
      const reportIdentifier = filing.accession_number || filing.filing_id;

      // Trigger company research for specific report
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/research`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cik: cik,
          companyName: companyName,
          companyId: companyId, // Add to existing company
          timeRange: '1year', // Not used when selectedReports provided
          selectedReports: [reportIdentifier], // Process only this specific report (using accession number)
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start report processing');
      }

      const result = await response.json();
      toast.success(`Processing started for ${filing.fiscal_period}`);

      // Refresh filing statuses after a delay
      setTimeout(() => {
        fetchQuarterlyFilings();
        onRefreshNeeded?.();
      }, 2000);
    } catch (error) {
      console.error('Error processing report:', error);
      toast.error('Failed to process report');
    } finally {
      setProcessingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportKey);
        return newSet;
      });
    }
  };

  const handleRefreshReport = async (filing: QuarterlyFiling) => {
    // Same as process, but for already processed reports
    await handleProcessReport(filing);
  };

  const handleRefreshFromSEC = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/quarterly/filings/US/${cik}/refresh`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to refresh filings from SEC');
      }

      const result = await response.json();
      toast.success(
        `Refreshed from SEC: ${result.new_filings} new, ${result.existing_filings} existing`
      );

      // Reload filings in silent mode (don't show error toast if this fails since we already succeeded)
      await fetchQuarterlyFilings(true);

      onRefreshNeeded?.();
    } catch (error) {
      console.error('Error refreshing from SEC:', error);
      toast.error('Failed to refresh from SEC');
    } finally {
      setRefreshing(false);
    }
  };

  const handleBatchProcessAllReports = async (skipProcessed: boolean) => {
    try {
      setBatchProcessing(true);

      // Calculate counts
      const processedCount = Object.values(filingStatuses).filter(
        v => v === 'Processed' || v === 'Partially Processed'
      ).length;
      const totalCount = filings.length;
      const unprocessedCount = totalCount - processedCount;

      // Show confirmation if reprocessing existing reports
      if (processedCount > 0 && !skipProcessed) {
        const confirmed = window.confirm(
          `⚠️ Reprocessing Warning\n\n` +
          `${processedCount} of ${totalCount} reports are already processed.\n\n` +
          `Reprocessing ALL ${totalCount} reports will:\n` +
          `• Take approximately ${totalCount * 3} minutes\n` +
          `• Cost approximately $${(totalCount * 0.25).toFixed(2)} in LLM fees\n\n` +
          `Recommendation: Use "Process Unprocessed" instead to save time and costs.\n\n` +
          `Click OK to reprocess all, or Cancel to go back.`
        );

        if (!confirmed) {
          setBatchProcessing(false);
          return;
        }
      }

      console.log(`Starting batch processing: skipProcessed=${skipProcessed}, timeRange=${timeRange}`);

      // Start batch processing with 2 minute timeout (job creation can be slow)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('[Batch Processing] Request timed out after 2 minutes');
        controller.abort();
      }, 120000); // 2 minutes

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cik: cik,
          companyName: companyName,
          ticker: companyId, // TODO: Get actual ticker if available
          companyId: companyId,
          timeRange: timeRange,
          skipProcessed: skipProcessed
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('[Batch Processing] Request completed successfully');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Handle skipped case (all reports already processed)
      if (result.status === 'skipped') {
        toast.info(result.message);
        setBatchProcessing(false);
        return;
      }

      // Success - store job ID and start polling
      setJobId(result.jobId);
      toast.success(`Batch processing started: ${result.totalReports} reports queued`);
      // Polling will start automatically via useEffect when jobId is set

    } catch (error: any) {
      console.error('[Batch Processing] Failed:', error);
      console.error('[Batch Processing] Error name:', error.name);
      console.error('[Batch Processing] Error message:', error.message);

      // Handle different error types
      if (error.name === 'AbortError') {
        console.log('[Batch Processing] Request was aborted (likely timeout)');
        toast.error('Request timed out after 2 minutes. The job may still be creating - please refresh to check status.');
      } else if (error.message?.includes('Failed to fetch')) {
        console.log('[Batch Processing] Network error');
        toast.error('Network error: Unable to reach the server. Please check your connection.');
      } else {
        console.log('[Batch Processing] Unknown error');
        toast.error(`Failed to start batch processing: ${error.message || 'Unknown error'}`);
      }

      setBatchProcessing(false);
    }
  };

  const timeRangeOptions: Array<{ value: string; label: string }> = [
    { value: '1year', label: 'Last 1 Year' },
    { value: '2years', label: 'Last 2 Years' },
    { value: '3years', label: 'Last 3 Years' },
    { value: '5years', label: 'Last 5 Years' },
    { value: '10years', label: 'Last 10 Years' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading quarterly filings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Time Range Selector and Action Buttons */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">
            Available Quarterly Reports
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600">Time Range:</span>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              options={timeRangeOptions}
              className="w-40"
            />
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshFromSEC}
            disabled={refreshing || batchProcessing}
          >
            {refreshing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Refresh from SEC
              </>
            )}
          </Button>

          {/* Batch Processing Buttons */}
          {filings.length > 0 && (() => {
            const processedCount = Object.values(filingStatuses).filter(
              v => v === 'Processed' || v === 'Partially Processed'
            ).length;
            const totalCount = filings.length;
            const unprocessedCount = totalCount - processedCount;

            return (
              <>
                {unprocessedCount > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleBatchProcessAllReports(true)}
                    disabled={batchProcessing || refreshing}
                  >
                    {batchProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Process Unprocessed ({unprocessedCount})
                      </>
                    )}
                  </Button>
                )}

                {processedCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBatchProcessAllReports(false)}
                    disabled={batchProcessing || refreshing}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reprocess All ({totalCount})
                  </Button>
                )}
              </>
            );
          })()}
        </div>

        {/* Warning Message */}
        {(() => {
          const processedCount = Object.values(filingStatuses).filter(
            v => v === 'Processed' || v === 'Partially Processed'
          ).length;
          const totalCount = filings.length;
          const unprocessedCount = totalCount - processedCount;

          if (processedCount > 0 && unprocessedCount > 0 && !batchProcessing) {
            return (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {processedCount} reports already processed. "Process Unprocessed" saves ~{processedCount * 3} minutes.
                </span>
              </div>
            );
          }
          return null;
        })()}

        {/* Batch Processing Progress Indicator (Phase 3) */}
        {jobProgress && batchProcessing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-blue-900">Batch Processing Progress</span>
              <span className="text-sm text-blue-700">
                {jobProgress.processedReports || 0} / {jobProgress.totalReports || 0} reports
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-blue-100 rounded-full h-2 mb-3">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${jobProgress.progress || 0}%` }}
              />
            </div>

            {/* Status Message */}
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
              <p className="text-sm text-blue-700">
                {jobProgress.message || 'Processing reports...'}
              </p>
            </div>

            {/* Current Report Info */}
            {jobProgress.currentReportIndex && (
              <p className="text-xs text-blue-600 mt-2">
                Current: Report {jobProgress.currentReportIndex} of {jobProgress.totalReports}
                {jobProgress.estimatedTimeRemaining && ` • Est. ${jobProgress.estimatedTimeRemaining} remaining`}
              </p>
            )}

            {/* Failed Reports Warning */}
            {jobProgress.failedReports > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm text-amber-700">
                <AlertCircle className="h-4 w-4" />
                <span>{jobProgress.failedReports} report{jobProgress.failedReports > 1 ? 's' : ''} failed</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filings Table */}
      <div className="space-y-3">
        {filings.length === 0 ? (
          <div className="text-center py-12 text-neutral-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-neutral-300" />
            <p>No quarterly filings found for selected time range</p>
          </div>
        ) : (
          filings.map((filing) => {
            const status = filingStatuses[filing.filing_id] || 'Not Processed';
            const reportDetails = filingDetails[filing.filing_id];
            const isProcessing = processingReports.has(filing.filing_id);

            // Calculate what's missing for partially processed reports
            const missingItems: string[] = [];
            if (status === 'Partially Processed' && reportDetails) {
              if (!reportDetails.xbrlExtracted) missingItems.push('XBRL Extraction');
              if (!reportDetails.aiExtracted) missingItems.push('AI Extraction');
              if (!reportDetails.segmentsExtracted) missingItems.push('Segment Analysis');
            }

            return (
              <div
                key={filing.filing_id}
                className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded bg-primary-100 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900">
                      {filing.fiscal_period}
                    </div>
                    <div className="text-sm text-neutral-600">
                      {filing.form_type} • Filed: {new Date(filing.filing_date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {status === 'Processed' ? (
                        <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3" />
                          <span>Processed</span>
                        </div>
                      ) : status === 'Partially Processed' ? (
                        <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700" title={missingItems.length > 0 ? `Missing: ${missingItems.join(', ')}` : ''}>
                          <AlertCircle className="h-3 w-3" />
                          <span>Partially Processed</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">
                          <Clock className="h-3 w-3" />
                          <span>Not Processed</span>
                        </div>
                      )}
                      <div className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                        {filing.source}
                      </div>
                    </div>
                    {/* Show extraction status details for partially processed reports */}
                    {status === 'Partially Processed' && reportDetails && (
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${reportDetails.xbrlExtracted ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {reportDetails.xbrlExtracted ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                          <span>XBRL</span>
                        </div>
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${reportDetails.aiExtracted ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {reportDetails.aiExtracted ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                          <span>AI</span>
                        </div>
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${reportDetails.segmentsExtracted ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {reportDetails.segmentsExtracted ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                          <span>Segments</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {status === 'Processed' || status === 'Partially Processed' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRefreshReport(filing)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleProcessReport(filing)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Process
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Summary */}
      {filings.length > 0 && (
        <div className="flex items-center justify-between text-sm text-neutral-600 pt-2 border-t">
          <span>
            Total: {filings.length} filings
          </span>
          <span>
            Processed: {Object.values(filingStatuses).filter(v => v === 'Processed').length} •
            Partially Processed: {Object.values(filingStatuses).filter(v => v === 'Partially Processed').length} •
            Not Processed: {Object.values(filingStatuses).filter(v => v === 'Not Processed').length}
          </span>
        </div>
      )}
    </div>
  );
}
